import React, { useEffect, useRef, useState } from 'react';
import { FormApi } from 'final-form';
import { FieldInputProps } from 'react-final-form';
import { Rating } from '@material-ui/lab';
import { IconContainerProps } from '@material-ui/lab/Rating';
import clsx from 'clsx';
import { Field } from '../../../helpers/renderCountDebug';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';

// Explicit request: the header's Quality / Likelihood badges save the moment a star is clicked, with no feedback. This
// owns that badge: the stars ripple and dim while the save runs, and the label swaps to Saving… / Saved ✓ / Not saved.
// The status lives in this component's own state so a click doesn't re-render the whole drawer.
type RatingSaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

const savedLabelDurationMs = 1800;
const failedLabelDurationMs = 2400;
const starPopStaggerMs = 70;
// A fast save would flash "Saving…" for a moment too short to read, so it stays at least this long.
const minimumSavingDisplayMs = 500;

const saveStatusLabels: Record<Exclude<RatingSaveStatus, 'idle'>, string> = {
    saving: 'Saving…',
    saved: 'Saved ✓',
    failed: 'Not saved',
};

// Stagger the ripple left to right: the animation delay follows the star's own position.
const RatingStarContainer: React.FC<IconContainerProps> = ({ value, ...containerProps }) => (
    <span {...containerProps} style={{ animationDelay: `${(value - 1) * starPopStaggerMs}ms` }} />
);

interface EnquiryRatingBadgeProps {
    fieldName: 'enquiryQuality' | 'likelihoodToProceed';
    label: string;
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    // The drawer's own onRatingChange: updates the form value and returns the save request's outcome (true = accepted).
    // tslint:disable-next-line: no-any
    onRatingChange: (name: string, isDirectCallMutation: boolean, input: FieldInputProps<any, HTMLElement>, form: FormApi<Enquiry>, values: any) => (event: React.ChangeEvent<{}>, value: number | null) => Promise<boolean> | void;
}

export const EnquiryRatingBadge: React.FC<EnquiryRatingBadgeProps> = ({ fieldName, label, form, classes, onRatingChange }) => {
    const [saveStatus, setSaveStatus] = useState<RatingSaveStatus>('idle');
    const [starPopCount, setStarPopCount] = useState(0);
    const isMountedRef = useRef(true);
    const latestRequestIdRef = useRef(0);
    const statusResetTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const statusDelayTimerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (statusResetTimerRef.current) {
                clearTimeout(statusResetTimerRef.current);
            }
            if (statusDelayTimerRef.current) {
                clearTimeout(statusDelayTimerRef.current);
            }
        };
    }, []);

    const showStatus = (status: RatingSaveStatus, resetAfterMs?: number) => {
        if (statusResetTimerRef.current) {
            clearTimeout(statusResetTimerRef.current);
        }
        setSaveStatus(status);
        if (resetAfterMs) {
            statusResetTimerRef.current = setTimeout(() => setSaveStatus('idle'), resetAfterMs);
        }
    };

    // tslint:disable-next-line: no-any
    const handleRatingChange = (input: FieldInputProps<any, HTMLElement>) => (event: React.ChangeEvent<{}>, value: number | null) => {
        const previousValue = form.getState().values[fieldName];
        const previousIsSavedDirectly = form.getState().values.isSavedDirectly;
        const requestId = ++latestRequestIdRef.current;
        const savingStartedAt = Date.now();

        const saveRequest = onRatingChange(fieldName, true, input, form, form.getState().values)(event, value);

        setStarPopCount((previousCount) => previousCount + 1);
        showStatus('saving');

        Promise.resolve(saveRequest)
            .then((isSaved) => isSaved !== false)
            .catch(() => false)
            .then((isSaved) => {
                // A later click owns the badge now - its own result decides what is shown.
                if (!isMountedRef.current || requestId !== latestRequestIdRef.current) {
                    return;
                }
                if (!isSaved) {
                    // The server never stored this value, so put the stars back to what it still has.
                    form.change(fieldName, previousValue);
                    form.change('isSavedDirectly', previousIsSavedDirectly);
                }

                if (statusDelayTimerRef.current) {
                    clearTimeout(statusDelayTimerRef.current);
                }
                statusDelayTimerRef.current = setTimeout(() => {
                    if (!isMountedRef.current || requestId !== latestRequestIdRef.current) {
                        return;
                    }
                    if (isSaved) {
                        showStatus('saved', savedLabelDurationMs);
                    } else {
                        showStatus('failed', failedLabelDurationMs);
                    }
                }, Math.max(0, minimumSavingDisplayMs - (Date.now() - savingStartedAt)));
            });
    };

    const currentLabel = saveStatus === 'idle' ? label : saveStatusLabels[saveStatus];
    const allLabels = [label, saveStatusLabels.saving, saveStatusLabels.saved, saveStatusLabels.failed];

    return (
        <div className={classes.infoBadge}>
            <span className={clsx(classes.infoBadgeLabel, classes.infoBadgeLabelStack, {
                [classes.infoBadgeLabelSaving]: saveStatus === 'saving',
                [classes.infoBadgeLabelSaved]: saveStatus === 'saved',
                [classes.infoBadgeLabelFailed]: saveStatus === 'failed',
            })}>
                {allLabels.map((text) => {
                    const isCurrent = text === currentLabel;
                    return (
                        <span
                            key={text}
                            aria-hidden={!isCurrent}
                            className={clsx(classes.infoBadgeLabelText, isCurrent ? classes.infoBadgeLabelSwap : classes.infoBadgeLabelHidden)}
                        >
                            {text}
                        </span>
                    );
                })}
            </span>
            <Field name={fieldName}>
                {({ input }) => (
                    <Rating
                        name={input.name}
                        size="small"
                        // tslint:disable-next-line: radix
                        value={parseInt(input.value)}
                        className={clsx({
                            [classes.ratingSaving]: saveStatus === 'saving',
                            [classes.ratingPopA]: starPopCount > 0 && starPopCount % 2 === 1,
                            [classes.ratingPopB]: starPopCount > 0 && starPopCount % 2 === 0,
                        })}
                        classes={{ iconFilled: classes.ratingStarFilled }}
                        IconContainerComponent={RatingStarContainer}
                        onChange={handleRatingChange(input)}
                    />
                )}
            </Field>
        </div>
    );
};
